import { useEffect, useState } from "react";
import { Maximize2 } from "lucide-react";
import { ViewsReview } from "./ViewsReview";
import { NotifyForm } from "./NotifyForm";
import { BuildViewsOverlay } from "./BuildViewsOverlay";
import { BuildFailedPanel } from "./BuildFailedPanel";
import { BuildRunningPanel } from "./BuildRunningPanel";
import { CancelBuildButton } from "./CancelBuildButton";
import { ImageLightbox } from "./ImageLightbox";
import { FrontImage } from "./FrontImage";
import { frontImage, blankOnError } from "../lib/api";

// Left column of Step 3: the front view. A photo duck pins its original photo top-left; click the thumbnail to swap
// which one fills the square. An uploaded-render duck IS its own front (its `photo` is just a smaller copy of the same
// upload), so the duplicate toggle is hidden and the upload fills the square. Click the big image (or the enlarge
// icon) to zoom in the lightbox.
const FrontFigure = ({ creation, review, ended }) => {
  const [showOriginal, setShowOriginal] = useState(false);
  const [lightbox, setLightbox] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  useEffect(() => setShowOriginal(false), [creation.id]);
  const front = frontImage(creation);
  const uploadedRender = creation.source === "render";
  const hasToggle = !!creation.photo && !uploadedRender;
  const main = showOriginal && hasToggle ? creation.photo : front;
  const thumb = showOriginal ? front : creation.photo;
  const images = (hasToggle ? [creation.photo, front] : [front]).filter(Boolean);
  const openLightbox = () => {
    setLightboxIndex(hasToggle && !showOriginal ? 1 : 0);
    setLightbox(true);
  };
  return (
    <figure data-testid="front-figure" className="rounded-3xl overflow-hidden border-2 border-primary bg-card duck-shadow relative self-start lg:sticky lg:top-24">
      <FrontImage
        creation={creation}
        src={main}
        alt={showOriginal ? "original photo" : "duck render"}
        testId="front-main-img"
        onClick={openLightbox}
        className="w-full aspect-square object-cover cursor-zoom-in"
      />
      {hasToggle && (
        <button
          type="button"
          data-testid="original-photo-thumb"
          aria-label={showOriginal ? "Show front view" : "Show original photo"}
          aria-pressed={showOriginal}
          onClick={() => setShowOriginal((v) => !v)}
          className="absolute top-3 left-3 w-[17.5%] rounded-2xl border-2 border-background overflow-hidden bg-card shadow-lg hover:ring-2 hover:ring-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent transition-shadow"
        >
          <img src={thumb} alt={showOriginal ? "rendered front view" : "original upload"} data-testid="original-photo-thumb-img" onError={blankOnError} className="w-full aspect-square object-cover" />
          <span className="absolute bottom-1 left-1 rounded-full bg-background/90 text-muted-foreground px-2 py-0.5 text-[8px] font-mono2 uppercase tracking-widest">
            {showOriginal ? "front" : "original"}
          </span>
        </button>
      )}
      <button
        type="button"
        data-testid="front-enlarge-btn"
        aria-label="Enlarge image"
        onClick={openLightbox}
        className="absolute top-3 right-3 grid place-items-center h-9 w-9 rounded-full bg-background/90 text-foreground shadow-lg hover:ring-2 hover:ring-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent transition-shadow"
      >
        <Maximize2 className="h-4 w-4" />
      </button>
      <figcaption data-testid="front-caption" className="absolute bottom-3 left-3 rounded-full bg-background/90 text-muted-foreground px-3 py-1 text-[10px] font-mono2 uppercase tracking-widest">
        {showOriginal ? "Original photo" : "Front view"}
      </figcaption>
      {!review && <BuildViewsOverlay creation={creation} />}
      {!ended && <div className="absolute inset-0 bg-gradient-to-t from-primary/30 to-transparent animate-pulse pointer-events-none" />}
      <ImageLightbox open={lightbox} onOpenChange={setLightbox} images={images} index={lightboxIndex} onIndexChange={setLightboxIndex} alt={showOriginal ? "original photo" : "front view"} />
    </figure>
  );
};

// Step 3 while the build is not ready: render preview on the left, a status panel per build_status on the right.
export const BuildProgress = ({ creation, onRetry, onRetryStronger, onBack, onNotify, onCancel, onUpdate, onExtend }) => {
  const review = creation.build_status === "views_review";
  const ended = creation.build_status === "failed" || creation.build_status === "cancelled";

  return (
    <div data-testid="build-progress" className="grid lg:grid-cols-[1fr_1.1fr] gap-8 items-start animate-pop">
      <FrontFigure creation={creation} review={review} ended={ended} />

      <div className="space-y-6">
        {review ? (
          <ViewsReview creation={creation} onUpdate={onUpdate} onBack={onBack} />
        ) : ended ? (
          <BuildFailedPanel creation={creation} onRetry={onRetryStronger || onRetry} onBack={onBack} />
        ) : (
          <BuildRunningPanel creation={creation} onRetry={onRetry} onCancel={onCancel} onExtend={onExtend} />
        )}
        {review && onCancel && <CancelBuildButton onCancel={onCancel} />}
        {!ended && !review && onNotify && <NotifyForm creation={creation} onNotify={onNotify} />}
      </div>
    </div>
  );
};
